import React, { FC, useState } from 'react'
import { Box, Button, CircularProgress } from '@mui/material'
import { SensorColumnsDataGrid } from 'src/components/columns/sensor'
import { Sensor } from 'src/api'
import { SensorListFilters } from 'src/components/forms/sensor'
import { SensorFilters } from 'src/enums'
import { useQueryParamsState, useAddPopupMessage } from 'src/hooks/ui'
import { useSensorListRequest } from 'src/hooks/api'
import { CommonSensorList } from './commonSensorsList'

const getColumns = () => [
  SensorColumnsDataGrid.Id(),
  SensorColumnsDataGrid.IMEI,
  SensorColumnsDataGrid.AvailableInThingspace,
  SensorColumnsDataGrid.ActivationStatus,
  SensorColumnsDataGrid.LastPing,
  SensorColumnsDataGrid.FirmwareVersion,
  SensorColumnsDataGrid.LastContractor,
  SensorColumnsDataGrid.CurrentlyAssigned,
  SensorColumnsDataGrid.DateAssigned,
  SensorColumnsDataGrid.Project,
  SensorColumnsDataGrid.Label,
  SensorColumnsDataGrid.DateAdded,
  SensorColumnsDataGrid.OrderDate,
  SensorColumnsDataGrid.OrderNumber,
  SensorColumnsDataGrid.OrderPart
]

interface UnusedSensorListProps {
  isLoading: boolean
  count: number
  data: Sensor[]
}

export const UnusedSensorList: FC<UnusedSensorListProps> = ({ data, isLoading, count }) => {
  const queryParams = useQueryParamsState()
  const addPopupMessage = useAddPopupMessage()
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false)
  const [isDownloadingTxt, setIsDownloadingTxt] = useState(false)

  const { refetch } = useSensorListRequest({
    params: queryParams,
    paramsTransform: [(params) => ({ ...params, limit: 100000, offset: 0 })],
    options: { enabled: false }
  })

  const fetchImeis = async (): Promise<string[]> => {
    const result = await refetch()
    return (result.data?.rows || []).map((s: Sensor) => s.imei || '')
  }

  const triggerDownload = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType })
    const href = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = href
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(href)
  }

  const downloadImeiCsv = async () => {
    try {
      setIsDownloadingCsv(true)
      const imeis = await fetchImeis()
      triggerDownload(['IMEI', ...imeis].join('\n'), `unused_sensors_imei_${new Date().toISOString()}.csv`, 'text/csv')
    } catch (error) {
      console.error(error)
      addPopupMessage({ text: 'Error during downloading IMEI list', type: 'error' })
    } finally {
      setIsDownloadingCsv(false)
    }
  }

  const downloadImeiTxt = async () => {
    try {
      setIsDownloadingTxt(true)
      const imeis = await fetchImeis()
      triggerDownload(imeis.join(','), `unused_sensors_imei_${new Date().toISOString()}.txt`, 'text/plain')
    } catch (error) {
      console.error(error)
      addPopupMessage({ text: 'Error during downloading IMEI list', type: 'error' })
    } finally {
      setIsDownloadingTxt(false)
    }
  }

  return (
    <>
      <Box mb={2} display={'flex'} justifyContent={'end'} gap={2}>
        <Button variant="contained" color="primary" size="large" disabled={isDownloadingCsv} onClick={downloadImeiCsv} sx={{ textTransform: 'none' }}>
          {isDownloadingCsv ? <CircularProgress color="info" size={26} /> : 'Download IMEI list as CSV'}
        </Button>
        <Button variant="contained" color="primary" size="large" disabled={isDownloadingTxt} onClick={downloadImeiTxt} sx={{ textTransform: 'none' }}>
          {isDownloadingTxt ? <CircularProgress color="info" size={26} /> : 'Download IMEIs'}
        </Button>
      </Box>
      <CommonSensorList
        columns={getColumns()}
        count={count}
        data={data}
        isLoading={isLoading}
        filters={
          <SensorListFilters
            activeFilters={[
              SensorFilters.LastPingMoreThan,
              SensorFilters.ExcludeStaging,
              SensorFilters.AvailableInThingspace,
              SensorFilters.ActivationStatus,
              SensorFilters.OrderNumber,
              SensorFilters.OrderDate
            ]}
          />
        }
        checkboxSelection
      />
    </>
  )
}
